/**
 * AI Metadata Section
 *
 * Displays AI-generated metadata (Vision API results) in Gallery Detail Modal.
 * Shows: file_name, alt_title, colors, objects, style, quality, composition.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Sparkles, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { AIMetadata } from '@/types';

export interface AIMetadataSectionProps {
  metadata: AIMetadata | null;
  fileName?: string;
  altTitle?: string;
}

export function AIMetadataSection({ metadata, fileName, altTitle }: AIMetadataSectionProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(true);

  if (!metadata && !fileName && !altTitle) {
    return null;
  }

  return (
    <div className="border-t border-neutral-200 dark:border-neutral-700 pt-4">
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 w-full text-left hover:opacity-80 transition"
        aria-expanded={expanded}
      >
        <Sparkles className="h-4 w-4 text-primary-500" />
        <span className="font-medium text-sm text-neutral-900 dark:text-white">
          AI-Generierte Metadaten
        </span>
        <ChevronDown
          className={cn(
            'h-4 w-4 text-neutral-500 transition-transform ml-auto',
            expanded && 'rotate-180'
          )}
        />
      </button>

      {/* Content */}
      {expanded && (
        <div className="mt-3 space-y-3 text-sm">
          {/* File Name */}
          {fileName && (
            <div>
              <span className="text-neutral-500 dark:text-neutral-400">Dateiname:</span>
              <p className="text-neutral-900 dark:text-white font-mono text-xs mt-1 break-all">
                {fileName}
              </p>
            </div>
          )}

          {/* Alt Text */}
          {altTitle && (
            <div>
              <span className="text-neutral-500 dark:text-neutral-400">Alt-Text (Barrierefreiheit):</span>
              <p className="text-neutral-700 dark:text-neutral-200 mt-1">{altTitle}</p>
            </div>
          )}

          {/* Colors */}
          {metadata?.colors?.palette && metadata.colors.palette.length > 0 && (
            <div>
              <span className="text-neutral-500 dark:text-neutral-400">Farbpalette:</span>
              <div className="flex gap-2 mt-1 flex-wrap items-center">
                {metadata.colors.dominant && (
                  <div className="flex items-center gap-1">
                    <div
                      className="w-6 h-6 rounded border border-neutral-300 dark:border-neutral-600"
                      style={{ backgroundColor: metadata.colors.dominant }}
                      title={`Dominant: ${metadata.colors.dominant}`}
                    />
                    <span className="text-xs text-neutral-500 dark:text-neutral-400">Dominant</span>
                  </div>
                )}
                {metadata.colors.palette.map((color, idx) => (
                  <div
                    key={idx}
                    className="w-6 h-6 rounded border border-neutral-300 dark:border-neutral-600"
                    style={{ backgroundColor: color }}
                    title={color}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Detected Objects */}
          {metadata?.detected_objects && metadata.detected_objects.length > 0 && (
            <div>
              <span className="text-neutral-500 dark:text-neutral-400">Erkannte Objekte:</span>
              <div className="flex gap-2 mt-1 flex-wrap">
                {metadata.detected_objects.map((obj, idx) => (
                  <span
                    key={idx}
                    className="px-2 py-1 rounded-full text-xs font-medium bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 border border-primary-200 dark:border-primary-800"
                  >
                    {obj}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Style Tags */}
          {metadata?.style_tags && metadata.style_tags.length > 0 && (
            <div>
              <span className="text-neutral-500 dark:text-neutral-400">Stil-Tags:</span>
              <div className="flex gap-2 mt-1 flex-wrap">
                {metadata.style_tags.map((tag, idx) => (
                  <span
                    key={idx}
                    className="px-2 py-1 rounded text-xs font-medium bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 border border-primary-200 dark:border-primary-800"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Image Quality */}
          {metadata?.image_quality && (
            <div>
              <span className="text-neutral-500 dark:text-neutral-400">{t('gallery.imageQuality')}</span>
              <div className="mt-1 text-sm text-neutral-700 dark:text-neutral-200">
                {metadata.image_quality.sharpness && (
                  <span className="mr-3">
                    {t('gallery.sharpness', { value: (metadata.image_quality.sharpness * 100).toFixed(0) })}
                  </span>
                )}
                {metadata.image_quality.brightness && (
                  <span>Helligkeit: {(metadata.image_quality.brightness * 100).toFixed(0)}%</span>
                )}
              </div>
            </div>
          )}

          {/* Composition */}
          {metadata?.composition && (
            <div>
              <span className="text-neutral-500 dark:text-neutral-400">Komposition:</span>
              <div className="mt-1 text-sm text-neutral-700 dark:text-neutral-200">
                {metadata.composition.perspective && (
                  <div>Perspektive: {metadata.composition.perspective}</div>
                )}
                {metadata.composition.lighting && (
                  <div>Beleuchtung: {metadata.composition.lighting}</div>
                )}
                {metadata.composition.background_type && (
                  <div>Hintergrund: {metadata.composition.background_type}</div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
